import { PerimetersService } from './../../../@core/utils/perimeters.service';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { GridApi, GridOptions } from 'ag-grid-enterprise';
import { Observable, Subscription, combineLatest } from 'rxjs';
import { ChartsService, InvestmentScenariiService, SpreadsheetColumnsService, UsersService } from '../../../@core/utils';
import { EmissionFactorService } from '../../../@core/utils/emission-factor.service';
import { KPI } from '../../../@core/api';
import { EmissionFactorListDialogComponent } from '../emission-factor-list-dialog/emission-factor-list-dialog.component';
import { NbDialogService } from '@nebular/theme';
import { map, switchMap } from 'rxjs/operators';
import { TABLE_FILTER_ICON } from '../../../@core/utils/constants';
import { AgGridAngular } from 'ag-grid-angular';
import { GROUP_PERMISSION_EDIT_ENERGY_SETTINGS } from '../../../@core/api/models';

@Component({
    selector: 'app-ag-grid-site-providers',
    templateUrl: './ag-grid-site-providers.component.html',
    styleUrls: ['./ag-grid-site-providers.component.scss'],
    standalone: true,
    imports: [AgGridAngular]
})
export class AgGridSiteProvidersComponent implements OnInit, OnDestroy {

  constructor(
    private translate: TranslateService,
    private spreadsheetColumnsService: SpreadsheetColumnsService,
    private emissionFactorService: EmissionFactorService,
    private investmentScenariiService: InvestmentScenariiService,
    private dialogService: NbDialogService,
    private chartService: ChartsService,
    private perimetersService: PerimetersService,
    private usersService: UsersService,
  ) { }

  @Input() public energyTypeId: number;

  public loading: boolean = false;
  public gridOptions: GridOptions = {};
  private subscriptions: Subscription[] = [];
  private gridApi: GridApi;
  private scenarii: KPI.InvestmentScenario[] = [];
  public canEdit: boolean = false;

  public ngOnInit() {
    this.usersService.hasCurrentUserPermissions([GROUP_PERMISSION_EDIT_ENERGY_SETTINGS]).subscribe((canEdit) => {
      this.canEdit = canEdit;
      this.gridOptions = {
        ...this.gridOptions,
        loading: false,
        context: this,
        rowHeight: 28,
        rowBuffer: 10, // Draw 10 rows above and below the visible rows (Performance)
        allowDragFromColumnsToolPanel: true, // Allow user to move columns from panel
        cellSelection: {
          suppressMultiRanges: true,
        },
        rowSelection: 'multiple', // Enable to select the entire row instead of a cell
        getRowId: params => params.data?.id?.toString(),
        suppressPropertyNamesCheck: true,
            columnMenu: 'new',
        defaultColDef: {
          sortable: true,
          resizable: true,
          editable: false,
          filter: true,
          icons: {
            filter: TABLE_FILTER_ICON,
          },
          enableCellChangeFlash: true, // Flash cell when changed
          menuTabs: ['filterMenuTab', 'generalMenuTab', 'columnsMenuTab'],
        },
        // sideBar: false,
        columnDefs: [], // Columns definition (Set it later)
        animateRows: false, // Row animations (when expanding)
        // Always show the column menus
        suppressMenuHide: true,
        groupHeaderHeight: 0, // Hide the columns groups in the header (Set it to 20 to show)
        // Restrict aggregations some functions (sum in an ag-grid native one)
        onGridReady: (params) => {
          this.gridApi = params.api;
          this.subscriptions.push(
            // Load initiative columns & scenarii
            combineLatest([
              this.investmentScenariiService.scenarii$,
              this.spreadsheetColumnsService.getAgGridSiteProvidersColumns()
            ]).pipe(
              switchMap(([scenarii, cols]) => {
                this.scenarii = scenarii;
                // Load columns
                this.gridApi.setGridOption('columnDefs', cols);
                return this.loadAgGridData();
              })
            ).subscribe((rowData) => {
              // Load data
              if (this.gridApi) {
                this.gridApi.setGridOption('rowData', rowData);
              }
            })
          );
        },
        // Hook executed after first data set
        onFirstDataRendered: () => {
          this.loading = false; // End the loading spinner
          this.subscriptions.push(
            this.perimetersService.perimeters$.subscribe((perimeters) => {
              this.gridApi?.getColumnFilterInstance('name')?.then((filterInstance) => {
                if (filterInstance) {
                  // Apply the set of values as a filter to the column
                  filterInstance.setModel({
                    filterType: 'set',
                    values: perimeters.map((perimeter) => perimeter.name),
                  });
                  this.gridApi?.onFilterChanged();
                }
              });
            }),
          );
          setTimeout(() => {
            this.gridApi.sizeColumnsToFit();
          }, 100);
        },
        onRowDataUpdated: () => {
          setTimeout(() => {
            this.gridApi.sizeColumnsToFit();
          }, 100);
        },
        getContextMenuItems: (params) => {
          return this.buildContextMenu(params);
        },
      };
    });
    this.subscriptions.push(
      this.spreadsheetColumnsService.getAgGridTranslations().subscribe((agGridTranslations) => {
        this.gridOptions.localeText = agGridTranslations;
      }),
    );
  }

  /**
   * Build the cells context menu
   */
  protected buildContextMenu(_row) {
    const contextMenu = []; // Initialize context menu

    const selectedRange = this.gridApi.getCellRanges()[0]; // Get selected range

    /**
     * Get data from selected rows
     */
    const rowNodes = [];

    if (
      typeof selectedRange !== 'undefined' &&
      typeof selectedRange.startRow !== 'undefined' &&
      typeof selectedRange.startRow.rowIndex !== 'undefined' &&
      typeof selectedRange.endRow.rowIndex !== 'undefined'
    ) {
      for (
        let i = selectedRange.startRow.rowIndex;
        i <= selectedRange.endRow.rowIndex;
        i++
      ) {
        this.gridApi.forEachNodeAfterFilterAndSort((rowNode, x) => {
          if (x === i && rowNode?.data) {
            rowNodes.push(rowNode);
          }
        });
      }
    }

    const sites: { id: number, name: string }[] = rowNodes.map((rowNode) => {
      if (rowNode?.data && rowNode.data.id) {
        return {
          id: Number(rowNode.data.id),
          name: rowNode.data.name,
        };
      }
    });

    const isMultipleRow = rowNodes.length > 1;

    const areAllWriteable = rowNodes.every((rowNode) => rowNode?.data?.writeable);

    /**
     * Cell editing
     */

    if (this.gridApi) {
      contextMenu.push({
        name: this.translate.instant('Change the emission factor'),
        disabled: !areAllWriteable || !rowNodes[0].data.writeable || !this.canEdit,
        subMenu: this.scenarii.map((scenario) => ({
          name: scenario.name,
          action: () => {
            this.seeEmissionFactorList(
              isMultipleRow ? null : rowNodes[0].data[`provider_scenario_id_${scenario.id}`],
              scenario.id,
              scenario.name,
              sites,
              rowNodes,
            );
          },
        })),
      });
    }

    return contextMenu;
  }

  private loadAgGridData(): Observable<any> {
    /**
     * Postponement data
     */
    return combineLatest([
      this.investmentScenariiService.scenarii$,
      this.emissionFactorService.emissionFactorProvidersBySite(this.energyTypeId)
    ]).pipe(
      map(([scenarii, res]) => {
        return res.data.map((site) => {
          const row = {
            id: site.id.toString(),
            name: site.name,
            cluster: site.cluster,
            writeable: site.writeable,
          };
          scenarii.forEach((scenario) => {
            const scenarioProvider = site.scenarios.find((s) => s.id === scenario.id)?.provider?.name;
            row[`provider_scenario_${scenario.id}`] = scenarioProvider;
            const scenarioProviderId = site.scenarios.find((s) => s.id === scenario.id)?.provider?.id;
            row[`provider_scenario_id_${scenario.id}`] = scenarioProviderId;
          });
          return row;
        });
      })
    );
  }


  private seeEmissionFactorList(
    currentProviderId: number,
    scenarioId: number,
    scenarioName: string,
    sites: { id: number, name: string }[],
    rowNodes: any[],
  ): void {

    const dialog = this.dialogService.open(
      EmissionFactorListDialogComponent,
      {
        autoFocus: false,
        context: {
          energyTypeId: this.energyTypeId,
          scenarioId,
          scenarioName,
          providerId: currentProviderId,
          sites,
          displayRadio: true,
        }
      }
    );
    dialog.componentRef.instance.changed.subscribe((provider: any) => {
      const idsToUpdate = rowNodes.map((rowNode) => rowNode.data.id);
      if (this.gridApi && provider) {
        const itemsToUpdate: any[] = [];
        this.gridApi.forEachNodeAfterFilterAndSort(function (rowNode, _index) {
          if (!idsToUpdate.includes(rowNode.data.id)) {
            return;
          }
          const data = rowNode.data;
          data[`provider_scenario_${scenarioId}`] = provider.name;
          itemsToUpdate.push(data);
        });
        this.gridApi.applyTransaction({ update: itemsToUpdate })!;
        this.chartService._carbonEmissionsHasChanged$.next();
      }
    })
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
  }
}
